import { useEffect } from 'react';
import { captureError } from '@/lib/logger';
import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';

interface UseOverdueInterestResult {
    /** SIGNED, as the engine reports it: negative when interest is owed. */
    totalOverdueInterest: number;
}

/**
 * Overdue interest for a contract, as `get_contract_insights` computed it.
 *
 * There is no client calculator any more — `calculate_overdue_interest` runs in the database and
 * every client reads the same number. What survives here is the one thing the payload cannot do
 * for itself: reporting the `rate_missing` tripwire, which the old calculator raised from inside
 * its own no-data branch. `contracts.overdue_interest_rate` is NOT NULL DEFAULT 365, so a true
 * flag means a defect upstream, never a contract that legitimately has no rate.
 */
export function useOverdueInterest(data: ContractInsightsData | undefined): UseOverdueInterestResult {
    const rateMissing = data?.overdue?.rate_missing ?? false;
    const contractId = data?.contract?.id;

    useEffect(() => {
        if (!rateMissing) return;
        captureError(
            new Error('Overdue interest not computed: contract has no overdue_interest_rate'),
            { tags: { area: 'overdue-interest' }, extra: { contractId } }
        );
    }, [rateMissing, contractId]);

    return { totalOverdueInterest: data?.overdue?.total_overdue_interest ?? 0 };
}
